import { createReducer, on } from '@ngrx/store';
import {
  VerificationMethod,
  VerificationSource,
} from '../../models/verification.model';
import * as VerificationActions from './verification.actions';

export const VERIFICATION_FEATURE_KEY = 'verification';

/**
 * Shared OTP / TOTP verification slice — feature-agnostic state used by
 * any flow that goes through the verification-management two-step
 * (transactions broadcast, approvals approve / reject, …).
 *
 * `source` identifies the originating flow so feature effects can pick
 * up `submitVerificationCodeSuccess` and dispatch their own confirmation
 * action without the verification slice knowing about each feature.
 */
export interface VerificationState {
  actionId: string | null;
  method: VerificationMethod | null;
  expiresAt: number | null;
  source: VerificationSource | null;

  submitting: boolean;
  error: string | null;
  attemptsRemaining: number | null;
  /**
   * The backend locked this handle after too many wrong codes. Nothing typed
   * against it will be accepted again; the modal must offer a restart.
   */
  locked: boolean;
  /**
   * The last code was a right authenticator code another approval already
   * spent. One code approves one action; the modal counts down to the next.
   */
  codeReused: boolean;
}

export const initialState: VerificationState = {
  actionId: null,
  method: null,
  expiresAt: null,
  source: null,

  submitting: false,
  error: null,
  attemptsRemaining: null,
  locked: false,
  codeReused: false,
};

export const verificationReducer = createReducer(
  initialState,

  on(
    VerificationActions.setVerificationChallenge,
    (state, { actionId, method, expiresAt, source }) => ({
      ...state,
      actionId,
      method,
      expiresAt,
      source,
      submitting: false,
      error: null,
      attemptsRemaining: null,
      locked: false,
      codeReused: false,
    })
  ),

  on(VerificationActions.clearVerificationChallenge, () => initialState),

  // A code for a challenge the slice no longer holds is not sent (see the
  // effect), so it must not leave the slice spinning either.
  on(
    VerificationActions.submitVerificationCode,
    (state, { actionId, source }) =>
      state.actionId === actionId && state.source === source
        ? { ...state, submitting: true, error: null, codeReused: false }
        : state
  ),

  on(VerificationActions.submitVerificationCodeSuccess, state => ({
    ...state,
    submitting: false,
    error: null,
    attemptsRemaining: null,
    locked: false,
    codeReused: false,
  })),

  on(
    VerificationActions.submitVerificationCodeFailure,
    (state, { error, attemptsRemaining, locked, codeReused }) => ({
      ...state,
      submitting: false,
      error,
      attemptsRemaining,
      locked: locked === true,
      codeReused: codeReused === true,
    })
  )
);
